import type { CSSProperties } from 'react';

import starCrystal from './assets/norvix-star.png';

/**
 * How the compass-star renders:
 * - `crystal` (default): the photoreal 3D crystalline mark exported 1:1 from Figma
 *   (raster, with the aurora glow baked in). Use on dark surfaces at display sizes.
 * - `gradient`: a crisp, infinitely-scalable SVG of the exact Figma star silhouette
 *   filled with the brand aurora gradient. Use when vector sharpness matters.
 * - `mono`: single-colour silhouette (honours `color` / currentColor). Use for
 *   watermarks, print, and tiny/monochrome contexts.
 */
export type CompassStarRender = 'crystal' | 'gradient' | 'mono';

interface CompassStarProps {
  size?: number;
  render?: CompassStarRender;
  /** Only used by the `mono`/`gradient` (SVG) renders. */
  color?: string;
  glow?: boolean;
  style?: CSSProperties | undefined;
  title?: string;
}

/** Native aspect ratio of the exported crystal PNG (incl. glow bleed). */
const CRYSTAL_RATIO = 793 / 807;

/**
 * Exact compass-star outline geometry, exported from Figma node 1:40
 * (372 × 379 user units). An 8-point compass: 4 long cardinal points +
 * 4 short diagonal points with faceted notches between them.
 */
const STAR_PATH =
  'M187.379 0 L165.113 144.436 L94.8096 95.044 L145.045 163.789 L0 184.578 L36.4348 189.5 L72.8956 195.078 L145.791 205.578 L94.5569 275.873 L103.232 269.806 L112.196 263.162 L120.582 257.096 L130.124 250.452 L165.876 225.498 L176.568 302.249 L182.174 340.58 L187.261 379 L192.005 340.58 L197.789 302.449 L208.959 225.446 L244.344 249.874 L262.272 262.873 L271.237 269.518 L279.333 275.295 L255.043 241.786 L248.682 233.12 L242.32 224.165 L230.175 206.543 L265.453 200.188 L300.731 194.989 L336.299 189.5 L353.938 186.9 L371.866 184.878 L302.611 173.895 L266.61 168.99 L248.393 166.39 L230.464 163.213 L254.754 128.548 L279.911 94.4611 L244.489 119.449 L226.994 132.303 L209.066 144.436 L187.379 0 Z';

let gradientSeq = 0;

export function CompassStar({
  size = 32,
  render = 'crystal',
  color = 'var(--aurora-500)',
  glow = false,
  style,
  title = 'Norvix',
}: CompassStarProps): JSX.Element {
  if (render === 'crystal') {
    return (
      <img
        src={starCrystal}
        alt={title}
        width={Math.round(size * CRYSTAL_RATIO)}
        height={size}
        style={{
          display: 'inline-block',
          height: size,
          width: 'auto',
          filter: glow ? 'drop-shadow(0 0 12px rgba(0, 201, 252, 0.45))' : undefined,
          ...style,
        }}
      />
    );
  }

  const gradientId = `norvix-aurora-${(gradientSeq += 1)}`;
  const fill = render === 'gradient' ? `url(#${gradientId})` : color;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 372 379"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      style={{
        display: 'inline-block',
        filter: glow ? 'drop-shadow(0 0 12px rgba(0, 201, 252, 0.55))' : undefined,
        ...style,
      }}
    >
      <title>{title}</title>
      {render === 'gradient' && (
        <defs>
          <linearGradient id={gradientId} x1="186" y1="0" x2="186" y2="379" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#00C9FC" />
            <stop offset="0.5" stopColor="#52EAFF" />
            <stop offset="1" stopColor="#0097D8" />
          </linearGradient>
        </defs>
      )}
      <path d={STAR_PATH} fill={fill} />
    </svg>
  );
}
